/**
 * One clock for every recorder artifact. Each MediaRecorder part gets a new
 * performance origin, while baseMs carries the logical session timeline
 * across reload recovery. Values never move backwards even if a test clock or
 * browser implementation does.
 *
 * It can be paused, and must be whenever the MediaRecorder is: the recorder
 * leaves a paused span out of the media, and the server cuts clips from the
 * media with these numbers, so a clock that kept running would shift every
 * cut after a pause by the time spent paused.
 */
export class MonotonicTimeline {
  private origin: number;
  private lastMs: number;
  /** When the current pause began, on the read clock; null while running. */
  private pausedAt: number | null = null;

  constructor(
    private readonly baseMs: number,
    private readonly readClock: () => number = () => performance.now(),
  ) {
    this.origin = readClock();
    this.lastMs = baseMs;
  }

  now(): number {
    if (this.pausedAt !== null) return this.lastMs;
    const candidate = this.baseMs + Math.max(0, Math.floor(this.readClock() - this.origin));
    this.lastMs = Math.max(this.lastMs, candidate);
    return this.lastMs;
  }

  peek(): number {
    return this.lastMs;
  }

  get paused(): boolean {
    return this.pausedAt !== null;
  }

  /** Stops the clock at this instant. A second call is a no-op. */
  pause(): void {
    if (this.pausedAt !== null) return;
    // One reading for both, so the time between two reads is not lost.
    const at = this.readClock();
    this.lastMs = Math.max(this.lastMs, this.baseMs + Math.max(0, Math.floor(at - this.origin)));
    this.pausedAt = at;
  }

  /** Starts the clock again from where it stopped, leaving the pause out. */
  resume(): void {
    if (this.pausedAt === null) return;
    this.origin += Math.max(0, this.readClock() - this.pausedAt);
    this.pausedAt = null;
  }
}
